// The pages behind a hidden clicks figure (P90, D206 of documentation/pr-plans/insights-simplify-plan.md): a hidden
// figure is itself the press that opens the dialog of its pages (Livewire's HiddenPages, OPEN_EVENT), and its tooltip
// previews its top pages. The previews of every figure on a screen come in one read (HiddenPages::peek()), asked for
// the first time a hidden figure's tooltip opens on that screen, never on the screen's first load, and kept here.
// A screen is its window: the funnels' every date, or Stats' period and search type (contextKey()).
//
// The press also names the screen it was made on (screenOf()), so a page moved from the dialog (P126, D244) marks every
// other open screen out of date and not this one, which reads again by itself.

import { screenOf } from "./writes.js";

export const STORE = "insightsHiddenPages";

/** HiddenPages::OPEN_EVENT, OPENER_EVENT, PEEK_EVENT and PEEKS_EVENT. */
export const OPEN_EVENT = "insights-hidden-pages";
export const OPENER_EVENT = "insights-hidden-pages-opener";
export const PEEK_EVENT = "insights-hidden-pages-peek";
export const PEEKS_EVENT = "insights-hidden-pages-peeks";

const LOADING = "loading";
const READY = "ready";
const FAILED = "failed";

/** The window a figure is read over, as HiddenPages::peek() keys it: the funnels' every date, or Stats' period. */
export function contextKey(detail) {
    if (!detail?.period) {
        return "funnel";
    }

    return ["stats", detail.period, detail.start ?? "", detail.end ?? "", detail.searchType ?? ""].join("|");
}

/** A figure's scope, as HiddenPagesAnswer::scopeKey() names it: `key:top`, `topic:12`, `placed` or `hidden`. */
export function scopeKey(detail) {
    if (detail.scope === "key") {
        return "key:" + detail.key;
    }

    return detail.scope === "topic" ? "topic:" + detail.topic : detail.scope;
}

/** Only the window, the part of a figure's detail its screen's previews are read over. */
function windowOf(detail) {
    return { searchType: detail?.searchType ?? null, period: detail?.period ?? null, start: detail?.start ?? null, end: detail?.end ?? null };
}

export default function HiddenPagesStore() {
    return {
        /** Each screen's read: LOADING, READY or FAILED, by contextKey(). */
        states: {},
        /** Each screen's previews, by contextKey(), then by scopeKey(): its top pages, each {page, clicks}. */
        peeks: {},

        init() {
            window.addEventListener(PEEKS_EVENT, (event) => this.answer(event.detail));
        },

        /** Ask for the screen's previews once, the first time one of its hidden figures shows its tooltip. */
        want(detail) {
            const context = contextKey(detail);

            if (this.states[context] === LOADING || this.states[context] === READY) {
                return;
            }

            this.states[context] = LOADING;
            window.Livewire?.dispatch(PEEK_EVENT, windowOf(detail));
        },

        /** A figure's top pages, [] once its screen's read has none for it or could not be made. */
        pages(detail) {
            return this.peeks[contextKey(detail)]?.[scopeKey(detail)] ?? [];
        },

        isLoading(detail) {
            return this.states[contextKey(detail)] === LOADING;
        },

        answer(detail) {
            this.peeks[detail.context] = detail.state === READY ? detail.peeks : {};
            this.states[detail.context] = detail.state === READY ? READY : FAILED;
        },

        /**
         * The press: the dialog on the figure's pages. $el is the press, which the dialog gives focus back to, and
         * $fallback the press of the tooltip it sits in, for when that tooltip has closed meanwhile.
         */
        open(el, detail, fallback = null) {
            window.dispatchEvent(new CustomEvent(OPENER_EVENT, { detail: { opener: el, fallback } }));
            window.Livewire?.dispatch(OPEN_EVENT, { ...detail, screen: screenOf(el) });
        },
    };
}
